import type { components, paths } from '@/types/hypervision_schema'
import type { components as gateway_components } from '@/types/gateway_schema'
import { defineStore } from 'pinia'
import unit_config from '@/data/graphic_units.json'
import { getDateStringYMD, getMonthInterval, getMonthsBetween } from '@/service/service_date'
import { useGatewayStore } from './store_gateway'
import type { ApplicationDevice } from '@/service/service_device'
import createClient from 'openapi-fetch'
import { useCookies } from '@/components/composable/composable_cookie'
import { useOrganizationUnitStore } from './store_organization_unit'
import { useUnitStore } from './store_unit'
import { useEventBus } from '@vueuse/core'

const bus = useEventBus('chart_fetch')

export type MonthPickerDate = { month: number; year: number }
type VariableOrSensor = components['schemas']['Variable'] | components['schemas']['Sensor']
export type Serie = ApexAxisChartSeries[0] & { code: keyof typeof unit_config; unit: string }
export type ChartDeviceData = ApplicationDevice & {
  selected: string[]
  series: Serie[]
  series_comp: Serie[]
}

type ChartBuilderStoreState = {
  id?: number | null
  name?: string
  fetching: boolean
  devices: ChartDeviceData[]
  selected_device?: string
  day_select: boolean
  compare: boolean
  dates: {
    base?: [Date | MonthPickerDate, Date | MonthPickerDate]
    comp?: [Date | MonthPickerDate, Date | MonthPickerDate]
  }
}

function getUnitLabel(unit: components['schemas']['Unit']) {
  switch (unit.code) {
    case 'M2':
      return 'm²'
    case 'MC':
      return 'm³'
    case 'm3J':
      return 'm³/j'
    case 'm3A':
      return 'm³/an'
    default:
      return unit.symbol
  }
}

const client = createClient<paths>({
  baseUrl: '/api'
})

export const useChartStore = defineStore('chart', {
  state: (): ChartBuilderStoreState => ({
    name: '',
    day_select: false,
    fetching: false,
    devices: [],
    compare: false,
    dates: {}
  }),
  getters: {
    getJWT: (_) => {
      const { getCookieValue } = useCookies()
      const cookie = getCookieValue('hv_jwt')
      return cookie
    },
    getIdString: (state) => {
      return state.id?.toString().padStart(5, '0')
    },
    getDevice: (state) => state.devices.find((e) => e.id === state.selected_device),
    getDevices: (state) => state.devices,
    /**
     * Return the parsed state as Series to be used by apex charts.
     * @returns An array containing all the series
     */
    getSeries: (state) => {
      return (type: 'base' | 'comp'): ApexAxisChartSeries => {
        const output: ApexAxisChartSeries = []
        for (const device of state.devices) {
          if (type === 'base') output.push(...device.series)
          else output.push(...device.series_comp)
        }

        return output
      }
    },
    /**
     * Get the Axis array for the chart
     * @param state
     */
    getAxis: (state) => {
      return (type: 'base' | 'comp'): ApexYAxis[] => {
        const output: ApexYAxis[] = []
        let i = 0
        for (const device of state.devices) {
          let series = type === 'base' ? device.series : device.series_comp
          for (const serie of series) {
            if (output.find((e) => e.seriesName === serie.code)) continue
            const configuration = unit_config[serie.code]
            output.push({
              seriesName: serie.code,
              opposite: configuration.position === 'right',
              decimalsInFloat: 2,
              title: { text: serie.name },
              tooltip: { enabled: true }
            })
          }
        }

        return output
      }
    },
    getValue: (state) => {
      return (id: string): VariableOrSensor => {
        const device = state.devices.find((e) => e.id === state.selected_device)
        if (!device) {
          throw 'Device not found'
        }
        const variable = device.variables.find((e) => e.variableId === id)
        if (variable) return variable

        const sensor = device.sensors.find((e) => e.sensorId === id)
        if (sensor) return sensor as VariableOrSensor
        throw `ERROR: Value ${id} does not exist.`
      }
    },
    getStringifiedBaseRange: (state): string[] => {
      if (!state.dates.base) {
        return []
      }

      return [getDateStringYMD(state.dates.base?.[0]), getDateStringYMD(state.dates.base?.[1])]
    },
    getStringifiedCompRange: (state): string[] | null => {
      if (!state.dates.comp || state.dates.comp === null || !state.compare) {
        return null
      }

      return [getDateStringYMD(state.dates.comp?.[0]), getDateStringYMD(state.dates.comp?.[1])]
    },
    /**
     * Checks if there is any select value in the devices list
     * @param state
     */
    isVisible: (state) => {
      for (const device of state.devices) {
        if (device.selected.length > 0) return true
      }

      return false
    },
    getStrokes: (state) => {
      return (type: 'base' | 'comp'): number[] => {
        const series: ApexAxisChartSeries = []
        for (const device of state.devices) {
          if (type === 'base') series.push(...device.series)
          else series.push(...device.series_comp)
        }

        const output = []
        for (const device of state.devices) {
          let series = type === 'base' ? device.series : device.series_comp
          for (const serie of series) {
            if (serie.type === 'column') output.push(0)
            else output.push(5)
          }
        }

        return output
      }
    },
    getMonths: (state) => {
      return (type: 'base' | 'comp'): string[] => {
        if (type === 'base') {
          const dates = state.dates.base
          if (!dates) return []
          return getMonthsBetween(dates[0], dates[1])
        } else {
          const dates = state.dates.comp
          if (!dates) return []
          return getMonthsBetween(dates[0], dates[1])
        }
      }
    }
  },
  actions: {
    async fetch(device_data?: ChartDeviceData) {
      let device = device_data
      if (!device_data) device = this.getDevice
      if (!this.dates.base || !device) return

      const unit_store = useUnitStore()
      const date_start = getDateStringYMD(this.dates.base[0])
      const date_end = getDateStringYMD(this.dates.base[1], true)
      this.fetching = true

      const series = []
      const series_comp = []
      for (const value of device.selected) {
        const measures = await this.fetchMeasures(value, date_start, date_end)
        if (!measures.data) continue

        const element = this.getValue(value)
        const unit = unit_store.getUnit(element.unitId)
        if (!unit) continue

        const code = unit.code as keyof typeof unit_config
        const configuration = unit_config[code]
        series.push({
          name: `${element.name} (${getUnitLabel(unit)})`,
          type: configuration.type === 'Line' ? 'line' : 'column',
          code: code,
          unit: unit.unitId!,
          data: measures.data.map((e) => {
            let date = e.measureDate

            if (configuration.type !== 'Line') {
              date = date.split('T')[0]
            }

            return {
              x: new Date(date).getTime(),
              y: e.decimalValue ?? 0
            }
          })
        })

        if (this.compare && this.dates.comp) {
          const date_start = getDateStringYMD(this.dates.comp[0])
          const date_end = getDateStringYMD(this.dates.comp[1], true)

          const measures = await this.fetchMeasures(value, date_start, date_end)
          if (!measures.data) continue

          const element = this.getValue(value)
          const unit = unit_store.getUnit(element.unitId)
          if (!unit) continue

          const code = unit.code as keyof typeof unit_config
          const configuration = unit_config[code]
          series_comp.push({
            name: `${element.name} (${getUnitLabel(unit)})`,
            type: configuration.type === 'Line' ? 'line' : 'column',
            code: code,
            unit: unit.unitId!,
            data: measures.data.map((e) => ({
              x: new Date(e.measureDate).getTime(),
              y: e.decimalValue ?? 0
            }))
          })
        }
      }

      device.series = series
      device.series_comp = series_comp
      this.fetching = false
      bus.emit()
    },
    async fetchAll() {
      return new Promise<void>(async (resolve) => {
        for (const device of this.devices) {
          await this.fetch(device)
        }
        resolve()
      })
    },
    /**
     * Retreive measures from an ID of a sensor or variable
     * @param id ID of the sensor
     * @param start DD-MM-YYYTHH:MM:SS
     * @param end DD-MM-YYYTHH:MM:SS
     */
    fetchMeasures(id: string, start: string, end: string) {
      const isvar = this.getDevice?.variables.find((e) => e.variableId === id) ? true : false
      if (isvar) {
        return this.fetchMeasuresVariable(id, start, end)
      }

      return this.fetchMeasuresSensor(id, start, end)
    },
    /**
     * Retreive measures from a given sensor id
     * @param sensor_id ID of the sensor
     * @param start DD-MM-YYYTHH:MM:SS
     * @param end DD-MM-YYYTHH:MM:SS
     */
    fetchMeasuresSensor(sensor_id: string, start: string, end: string) {
      const request = client.GET('/api/Measures/Acquisition', {
        params: {
          query: {
            sensorId: sensor_id,
            dateDebut: start,
            dateFin: end
          }
        },
        headers: {
          Authorization: `Bearer ${this.getJWT.value}`
        }
      })

      return request
    },
    /**
     * Retreive measures from a variable ID
     */
    fetchMeasuresVariable(variable_id: string, start: string, end: string) {
      const request = client.GET('/api/Measures/Variable', {
        params: {
          query: {
            variableId: variable_id,
            dateDebut: start,
            dateFin: end
          }
        },
        headers: {
          Authorization: `Bearer ${this.getJWT.value}`
        }
      })
      return request
    },
    select(id: string) {
      const index = this.getDevice?.selected.findIndex((e) => e === id)
      if (index === undefined) return
      if (index === -1) this.getDevice?.selected.push(id)
      else this.getDevice?.selected.splice(index, 1)
    },
    setDevice(device: ApplicationDevice) {
      this.selected_device = device.id
    },
    addDevice(device: ApplicationDevice) {
      this.devices.push({
        ...device,
        selected: [],
        series: [],
        series_comp: []
      })
      this.selected_device = device.id
    },
    removeDevice(device: ApplicationDevice) {
      this.devices = this.devices.filter((d) => d.id !== device.id)
    },
    init() {
      this.id = undefined
      this.name = undefined
      this.devices = []
      const date = getMonthInterval(new Date())
      this.dates.base = [new Date(date.first), new Date(date.last)]
      this.dates.comp = undefined
      this.compare = false
      this.selected_device = undefined
    },
    load(chart: gateway_components['schemas']['ChartDTO']) {
      this.id = chart.id
      this.name = chart.name ?? undefined
      this.day_select = chart.day_date
      if (this.day_select) {
        const dates_base: [Date, Date] = [
          new Date(chart.range_base[0]),
          new Date(chart.range_base[1])
        ]
        this.dates.base = dates_base
      } else {
        const start = new Date(chart.range_base[0])
        const end = new Date(chart.range_base[1])
        const dates_base: [MonthPickerDate, MonthPickerDate] = [
          { month: start.getMonth(), year: start.getFullYear() },
          { month: end.getMonth(), year: end.getFullYear() }
        ]
        this.dates.base = dates_base
      }

      if (chart.range_comp) {
        if (this.day_select) {
          const dates_comp: [Date, Date] = [
            new Date(chart.range_base[0]),
            new Date(chart.range_base[1])
          ]
          this.dates.comp = dates_comp
        } else {
          const start = new Date(chart.range_base[0])
          const end = new Date(chart.range_base[1])
          const dates_comp: [MonthPickerDate, MonthPickerDate] = [
            { month: start.getMonth(), year: start.getFullYear() },
            { month: end.getMonth(), year: end.getFullYear() }
          ]
          this.dates.base = dates_comp
        }

        this.compare = true
      } else {
        this.dates.comp = undefined
      }

      this.devices = []

      const store_organization_unit = useOrganizationUnitStore()
      for (const device_id of chart.devices) {
        const device = store_organization_unit.getDeviceById(device_id)
        if (!device) continue

        const chart_device: ChartDeviceData = {
          ...device,
          selected: [],
          series: [],
          series_comp: []
        }

        for (const variable of chart.variables) {
          if (device.variables?.find((e) => e.variableId === variable)) {
            chart_device.selected.push(variable)
          }
        }

        for (const sensor of chart.sensors) {
          if (device.sensors?.find((e) => e.sensorId === sensor)) {
            chart_device.selected.push(sensor)
          }
        }

        this.devices.push(chart_device)
      }

      this.selected_device = this.devices[0].id
      return this.fetchAll()
    },
    save() {
      const gateway = useGatewayStore()
      this.fetching = true

      const chart: gateway_components['schemas']['ChartDTO'] = {
        id: this.id,
        name: this.name,
        devices: [],
        sensors: [],
        variables: [],
        range_base: this.getStringifiedBaseRange,
        range_comp: this.getStringifiedCompRange,
        day_date: this.day_select
      }

      for (const device of this.devices) {
        for (const value of device.selected) {
          if (device.variables?.find((e) => e.variableId === value)) {
            chart.variables.push(value)
          } else {
            chart.sensors.push(value)
          }
        }

        chart.devices.push(device.id!)
      }

      if (chart.id === undefined) {
        gateway.postChart(chart).finally(() => {
          this.fetching = false
        })
      } else {
        gateway.patchChart(chart.id!, chart).finally(() => {
          this.fetching = false
        })
      }
    }
  }
})
